/**
 * 主题色 token 生成工具。
 *
 * 职责：把全局配置中的主色（IConfig.Color）派生为一组稳定的语义 token，
 * 并转换为可供 CSS 消费的变量。全站主题色只从这一处推导，后续换色时
 * 只需修改主色源，所有消费 CSS 变量的 UI 会整体跟随变化。
 */
import { generate } from '@ant-design/colors';

/**
 * 主题色语义 token。
 *
 * 字段与 Naive UI themeOverrides 的主色语义一一对应，
 * 同时补充 RGB 通道供需要透明度的场景使用。
 */
export interface ThemeColorTokens {
  /** 主色。 */
  primary: string;
  /** 主色 hover 态（比主色浅一档）。 */
  hover: string;
  /** 主色按下态（比主色深两档）。 */
  pressed: string;
  /** 主色补充色（比主色深一档）。 */
  suppl: string;
  /** 主色 RGB 通道字符串，形如 "38 105 220"，供 rgb(var(--puppy-primary-rgb) / 0.1) 使用。 */
  primaryRgbChannels: string;
}

/**
 * 主题色 token 对应的 CSS 变量名。
 *
 * 变量名集中定义，避免注入器与消费组件各自手写字符串造成漂移。
 */
export const THEME_COLOR_CSS_VARIABLE_NAMES = {
  primary: '--puppy-primary',
  hover: '--puppy-primary-hover',
  pressed: '--puppy-primary-pressed',
  suppl: '--puppy-primary-suppl',
  primaryRgbChannels: '--puppy-primary-rgb',
} as const;

/**
 * 把 #RRGGBB / #RGB 形式的十六进制颜色转为 "R G B" 通道字符串。
 *
 * @param hex 十六进制颜色值，调用方保证是合法格式。
 * @returns 空格分隔的十进制 RGB 通道字符串。
 */
function hexToRgbChannels(hex: string): string {
  const normalized = hex.replace('#', '');
  const full = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;

  const red = Number.parseInt(full.slice(0, 2), 16);
  const green = Number.parseInt(full.slice(2, 4), 16);
  const blue = Number.parseInt(full.slice(4, 6), 16);

  return `${red} ${green} ${blue}`;
}

/**
 * 根据主色生成主题色 token。
 *
 * 派生规则与 `@ant-design/colors` 的 `generate()` 色板约定保持一致：
 * - hover 取第 5 档（colors[4]），比主色浅；
 * - suppl 取第 7 档（colors[6]），比主色深一档；
 * - pressed 取第 8 档（colors[7]），比主色深两档。
 *
 * @param color 全局配置中的主色对象。
 * @returns 派生后的主题色 token。
 */
export function createThemeColorTokens(color: IConfig.Color): ThemeColorTokens {
  const palette = generate(color.hex);

  return {
    primary: color.hex,
    hover: palette[4] ?? color.hex,
    suppl: palette[6] ?? color.hex,
    pressed: palette[7] ?? color.hex,
    primaryRgbChannels: hexToRgbChannels(color.hex),
  };
}

/**
 * 把主题色 token 转换为 CSS 变量键值对。
 *
 * @param tokens 主题色 token。
 * @returns 以 CSS 变量名为键、颜色值为值的映射。
 */
export function themeColorTokensToCssVariables(tokens: ThemeColorTokens): Record<string, string> {
  return {
    [THEME_COLOR_CSS_VARIABLE_NAMES.primary]: tokens.primary,
    [THEME_COLOR_CSS_VARIABLE_NAMES.hover]: tokens.hover,
    [THEME_COLOR_CSS_VARIABLE_NAMES.pressed]: tokens.pressed,
    [THEME_COLOR_CSS_VARIABLE_NAMES.suppl]: tokens.suppl,
    [THEME_COLOR_CSS_VARIABLE_NAMES.primaryRgbChannels]: tokens.primaryRgbChannels,
  };
}
